/**
 * 故事流程图布局算法
 * 统一的布局逻辑，供 StoryEditor 和 AIGuidedCreation 共同使用
 * 
 * 核心规则：
 * 1. M主线水平排列在 row 0
 * 2. E1结局在主线行，E2/E3/E4在对应L支线行
 * 3. 顶层L分支"最新优先行"：L2占row 1，L1整体下移到row 2
 * 4. 支持多级L系列节点（L1-1, L2-1-1等）
 * 5. 碰撞检测与避让，子分支跟随父分支级联下移
 */

/**
 * 计算流程图节点位置
 * @param {Object} storyStructure - 故事结构
 * @param {Array} storyStructure.nodes - 节点数组，每个节点有 id 属性
 * @param {Array} storyStructure.connections - 连接数组，每个连接有 from 和 to 属性
 * @param {Object} options - 可选配置
 * @param {number} options.horizontalSpacing - 水平间距（默认450）
 * @param {number} options.verticalSpacing - 垂直间距（默认300）
 * @param {number} options.baseX - 起始X坐标（默认200）
 * @param {number} options.baseY - 起始Y坐标（默认150）
 * @returns {Map} nodeId -> {x, y} 的位置映射
 */
export const calculateNodePositions = (storyStructure, options = {}) => {
  const {
    horizontalSpacing = 450,
    verticalSpacing = 300,
    baseX = 200,
    baseY = 150
  } = options

  const nodePositions = new Map()
  const nodeRows = new Map()
  const nodeCols = new Map()

  console.log('🎨 主线-分支层级布局算法（统一版本）...')

  // 找到开始节点
  const startNode = storyStructure.nodes.find(n => 
    !storyStructure.connections.some(c => c.to === n.id)
  ) || storyStructure.nodes[0]

  if (!startNode) {
    console.warn('⚠️ 未找到开始节点')
    return nodePositions
  }

  console.log('🎯 开始节点:', startNode.id)

  // ========== 数据结构定义 ==========
  const nodeBranch = new Map() // nodeId -> branchName (如: 'M', 'L1', 'L2', 'L3')
  const branchNodes = new Map() // branchName -> [nodeIds]
  const branchParent = new Map() // branchName -> parentBranchName
  const branchChildren = new Map() // branchName -> [childBranchNames]
  const branchAppearOrder = [] // 分支出现的顺序

  // ========== 第一步：BFS遍历，识别主线和所有分支 ==========
  console.log('\n📝 第一步：识别主线和分支结构...')
  
  const queue = [{ nodeId: startNode.id, row: 0, col: 0, branchName: 'M' }]
  const visited = new Set()
  let nextLBranchNumber = 1 // L分支计数器

  // 初始化M主线
  branchNodes.set('M', [])
  branchParent.set('M', null)
  branchChildren.set('M', [])
  branchAppearOrder.push('M')

  while (queue.length > 0) {
    const { nodeId, row, col, branchName } = queue.shift()

    if (visited.has(nodeId)) continue
    visited.add(nodeId)

    // 记录节点归属
    nodeBranch.set(nodeId, branchName)
    branchNodes.get(branchName).push(nodeId)

    // 记录基准位置
    nodeRows.set(nodeId, row)
    nodeCols.set(nodeId, col)

    console.log(`  ${nodeId}: baseRow=${row}, col=${col}, branch=${branchName}`)

    // 查找子节点
    const childConnections = storyStructure.connections.filter(c => c.from === nodeId)

    if (childConnections.length === 0) {
      // 叶子节点
      continue
    } else if (childConnections.length === 1) {
      // 单一连接，继续当前分支
      const childNodeId = childConnections[0].to
      if (!visited.has(childNodeId)) {
        queue.push({
          nodeId: childNodeId,
          row: row,
          col: col + 1,
          branchName: branchName
        })
      }
    } else {
      // 分支点
      console.log(`  🔀 分支点 ${nodeId} (分支: ${branchName})`)
      
      childConnections.forEach((conn, index) => {
        const childNodeId = conn.to
        if (visited.has(childNodeId)) return

        // 优先检查是否是M系列或结局节点
        const isMainNode = /^M\d+$/.test(childNodeId)
        const isEndingNode = /^E\d+$/.test(childNodeId)
        
        if (isMainNode && branchName === 'M') {
          // M系列节点：继续M主线
          queue.push({
            nodeId: childNodeId,
            row: 0,
            col: col + 1,
            branchName: 'M'
          })
          console.log(`    ➡️ 继续M主线: ${childNodeId}`)
        } else if (isEndingNode) {
          // 结局节点：E1 属于主线，其余(E2+)属于对应的 L 分支
          const endingNum = parseInt(childNodeId.replace('E', ''))
          if (endingNum === 1 && branchName === 'M') {
            queue.push({ nodeId: childNodeId, row: 0, col: col + 1, branchName: 'M' })
            console.log('    🎬 主线结局 E1 放置在主线行 row 0')
          } else {
            // 将 E{n} 归属到 L{n-1} 分支（顶层），如果当前已在某个 L 分支，则保持在该分支
            let targetBranchName = branchName.startsWith('L') ? branchName : `L${Math.max(1, endingNum - 1)}`
            if (!branchNodes.has(targetBranchName)) {
              branchNodes.set(targetBranchName, [])
              branchParent.set(targetBranchName, 'M')
              if (!branchChildren.has('M')) branchChildren.set('M', [])
              branchChildren.get('M').push(targetBranchName)
              branchAppearOrder.push(targetBranchName)
              // 对齐分支计数器
              const lNumber = parseInt(targetBranchName.replace('L', ''))
              if (lNumber >= nextLBranchNumber) nextLBranchNumber = lNumber + 1
            }
            const newRow = branchName === 'M' ? 1 : row
            queue.push({ nodeId: childNodeId, row: newRow, col: col + 1, branchName: targetBranchName })
            console.log(`    🎬 分支结局 ${childNodeId} 归属 ${targetBranchName} 行(row=${newRow})`)
          }
        } else {
          // 检查子节点是否已经有明确的L系列归属（支持多级：L1-1, L2-1-1, L2-2-1等）
          // 匹配模式：L{n}-{m} 或 L{n}-{m}-{k}
          const lSeriesMatch = childNodeId.match(/^(L\d+)(?:-\d+)+$/)
          let targetBranchName = null
          
          if (lSeriesMatch) {
            // 子节点有L系列编号，提取顶级分支名（如 L1, L2, L3）
            const lSeriesName = lSeriesMatch[1] // 'L1', 'L2', 'L3'...
            
            // 查找是否已经有这个L系列分支
            if (branchNodes.has(lSeriesName)) {
              // 已存在，归属到现有分支
              targetBranchName = lSeriesName
              console.log(`    ↪️ 归属到现有分支 ${lSeriesName}: ${childNodeId}`)
            } else {
              // 不存在，创建新分支并用L系列名称
              targetBranchName = lSeriesName
              
              branchNodes.set(targetBranchName, [])
              branchParent.set(targetBranchName, branchName)
              if (!branchChildren.has(branchName)) {
                branchChildren.set(branchName, [])
              }
              branchChildren.get(branchName).push(targetBranchName)
              branchAppearOrder.push(targetBranchName)
              
              // 更新L分支计数器
              const lNumber = parseInt(lSeriesName.replace('L', ''))
              if (lNumber >= nextLBranchNumber) {
                nextLBranchNumber = lNumber + 1
              }
              
              console.log(`    🌿 创建新分支 ${targetBranchName} (基于节点ID, 父分支: ${branchName})`)
            }
          } else {
            // 子节点没有明确的L系列归属，创建新分支
            targetBranchName = `L${nextLBranchNumber}`
            nextLBranchNumber++

            branchNodes.set(targetBranchName, [])
            branchParent.set(targetBranchName, branchName)
            if (!branchChildren.has(branchName)) {
              branchChildren.set(branchName, [])
            }
            branchChildren.get(branchName).push(targetBranchName)
            branchAppearOrder.push(targetBranchName)
            
            console.log(`    🌿 创建新分支 ${targetBranchName} (父分支: ${branchName})`)
          }

          // 计算新节点的基准行号
          let newRow
          if (branchName === 'M') {
            newRow = 1 // 从M主线出来的所有分支初始都在row 1
          } else {
            // 从L分支出来的分支，在父分支基础上+1
            newRow = row + 1
          }

          queue.push({
            nodeId: childNodeId,
            row: newRow,
            col: col + 1,
            branchName: targetBranchName
          })
          console.log(`    → 节点 ${childNodeId} 加入分支 ${targetBranchName} (row=${newRow})`)
        }
      })
    }
  }

  console.log('\n📊 分支结构分析:')
  console.log('  分支出现顺序:', branchAppearOrder)
  for (const [branchName, nodes] of branchNodes.entries()) {
    const parent = branchParent.get(branchName)
    const children = branchChildren.get(branchName) || []
    console.log(`  ${branchName}: ${nodes.length}个节点, 父分支=${parent}, 子分支=[${children.join(', ')}]`)
  }

  // ========== 第二步：优先行规则 + 碰撞检测 ==========
  console.log('\n🔍 第二步：优先行规则 + 碰撞检测...')
  
  const branchOffsets = new Map() // branchName -> offset
  branchOffsets.set('M', 0) // M主线不移动

  // 顶层分支"最新优先行"规则：
  // 从 M 主线分出的顶层 L 分支，按出现顺序排列，越新的越靠上（row 1），
  // 之前的顶层分支依次整体下移一行。
  const topLevelBranches = (branchChildren.get('M') || []).filter(Boolean)
  if (topLevelBranches.length > 0) {
    topLevelBranches.forEach((branchName, index) => {
      const baseShift = topLevelBranches.length - 1 - index // 最新的index最大，shift=0
      branchOffsets.set(branchName, baseShift)
      if (baseShift > 0) {
        console.log(`  ⬇️ 顶层分支预偏移: ${branchName} 下移 ${baseShift} 行 (最新优先)`) 
      } else {
        console.log(`  ⬆️ 顶层分支保持row 1: ${branchName}`)
      }
    })
  }

  // 按分支出现顺序处理（从后往前）
  for (let i = branchAppearOrder.length - 1; i >= 0; i--) {
    const currentBranch = branchAppearOrder[i]
    if (currentBranch === 'M') continue // M主线不检测

    const currentNodes = branchNodes.get(currentBranch) || []
    const currentOffset = branchOffsets.get(currentBranch) || 0
    
    console.log(`\n  检查分支 ${currentBranch} (${currentNodes.length}个节点, 当前offset=${currentOffset})`)

    // 检查是否与之前的分支冲突
    for (let j = 0; j < i; j++) {
      const earlierBranch = branchAppearOrder[j]
      if (earlierBranch === 'M') continue

      const earlierNodes = branchNodes.get(earlierBranch) || []
      const earlierOffset = branchOffsets.get(earlierBranch) || 0

      // 检查是否有列重叠
      for (const currentNodeId of currentNodes) {
        const currentCol = nodeCols.get(currentNodeId)
        const currentBaseRow = nodeRows.get(currentNodeId)
        const currentFinalRow = currentBaseRow + currentOffset

        for (const earlierNodeId of earlierNodes) {
          const earlierCol = nodeCols.get(earlierNodeId)
          const earlierBaseRow = nodeRows.get(earlierNodeId)
          const earlierFinalRow = earlierBaseRow + earlierOffset

          // 同列且行号冲突
          if (currentCol === earlierCol && Math.abs(currentFinalRow - earlierFinalRow) < 1) {
            // 需要下移早期分支（及其所有子分支）
            const newOffset = currentFinalRow - earlierBaseRow + 1
            
            if (newOffset > earlierOffset) {
              branchOffsets.set(earlierBranch, newOffset)
              console.log(`    ⬇️ ${earlierBranch} 需要避让 ${currentBranch}`)
              console.log(`      冲突: ${currentNodeId}(row=${currentFinalRow}) vs ${earlierNodeId}(row=${earlierFinalRow})`)
              console.log(`      ${earlierBranch} offset: ${earlierOffset} → ${newOffset}`)

              // 递归下移该分支的所有子分支
              const shiftChildBranches = (parentBranch, additionalOffset) => {
                const children = branchChildren.get(parentBranch) || []
                for (const childBranch of children) {
                  const childCurrentOffset = branchOffsets.get(childBranch) || 0
                  const childNewOffset = childCurrentOffset + additionalOffset
                  branchOffsets.set(childBranch, childNewOffset)
                  console.log(`      ⬇️⬇️ 子分支 ${childBranch} 跟随下移: ${childCurrentOffset} → ${childNewOffset}`)
                  // 递归处理孙分支
                  shiftChildBranches(childBranch, additionalOffset)
                }
              }

              shiftChildBranches(earlierBranch, newOffset - earlierOffset)
            }
          }
        }
      }
    }
  }

  // ========== 第三步：应用偏移量 ==========
  console.log('\n📐 第三步：应用偏移量...')
  for (const [nodeId, baseRow] of nodeRows.entries()) {
    const branchName = nodeBranch.get(nodeId)
    const offset = branchOffsets.get(branchName) || 0
    const finalRow = baseRow + offset
    nodeRows.set(nodeId, finalRow)
    console.log(`  ${nodeId}: baseRow=${baseRow} + offset=${offset} = finalRow=${finalRow} (分支=${branchName})`)
  }

  // ========== 第四步：转换为实际坐标 ==========
  storyStructure.nodes.forEach(node => {
    const row = nodeRows.get(node.id) || 0
    const col = nodeCols.get(node.id) || 0
    
    const x = baseX + col * horizontalSpacing
    const y = baseY + row * verticalSpacing
    
    nodePositions.set(node.id, { x, y })
  })

  console.log('✅ 布局计算完成')
  return nodePositions
}

/**
 * 从节点位置Map转换为对象格式（方便序列化）
 * @param {Map} positionsMap - calculateNodePositions 返回的Map
 * @returns {Object} nodeId -> {x, y} 的普通对象
 */
export const positionsMapToObject = (positionsMap) => {
  const obj = {}
  for (const [nodeId, position] of positionsMap.entries()) {
    obj[nodeId] = position
  }
  return obj
}

/**
 * 从对象格式转换为Map（方便使用）
 * @param {Object} positionsObj - 位置对象
 * @returns {Map} nodeId -> {x, y} 的Map
 */
export const positionsObjectToMap = (positionsObj) => {
  const map = new Map()
  for (const [nodeId, position] of Object.entries(positionsObj)) {
    map.set(nodeId, position)
  }
  return map
}

export default {
  calculateNodePositions,
  positionsMapToObject,
  positionsObjectToMap
}

